import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { Camera, Loader, X } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import './BrokerAvatarUpload.css';

export default function BrokerAvatarUpload({ currentAvatarUrl, userId, brokerId, userEmail, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [preview, setPreview] = useState(currentAvatarUrl);
  const [error, setError] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(currentAvatarUrl);
  }, [currentAvatarUrl]);

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const ext = file.name.split('.').pop().toLowerCase();
      const fileName = `${userId}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { cacheControl: '0', upsert: true });

      if (uploadError) throw uploadError;

      const { data: publicData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const publicUrl = publicData.publicUrl;
      const displayUrl = `${publicUrl}?t=${Date.now()}`;

      // Save clean URL to DB; use cache-busted URL for immediate display.
      const brokerWrite = brokerId
        ? supabase.from('brokers').update({ avatar_url: publicUrl }).eq('id', brokerId)
        : userEmail
          ? supabase.from('brokers').update({ avatar_url: publicUrl }).eq('email', userEmail)
          : Promise.resolve({ error: null });

      const [profileUpdate, brokerUpdate] = await Promise.all([
        supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', userId),
        brokerWrite,
      ]);

      if (profileUpdate.error) throw profileUpdate.error;
      if (brokerUpdate.error) throw brokerUpdate.error;

      setPreview(displayUrl);
      if (onUploadComplete) onUploadComplete(displayUrl);
    } catch (err) {
      console.error('Avatar upload failed:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    setShowRemoveConfirm(false);
    setRemoving(true);
    setError(null);
    try {
      // 1. Delete stored file(s) — stable name is `${userId}/avatar.<ext>`, ext unknown.
      const { data: files } = await supabase.storage.from('avatars').list(userId, { limit: 10 });
      if (files && files.length > 0) {
        const paths = files
          .filter(f => f.name.startsWith('avatar.'))
          .map(f => `${userId}/${f.name}`);
        if (paths.length > 0) {
          const { error: storageError } = await supabase.storage.from('avatars').remove(paths);
          if (storageError) console.warn('Storage delete error (non-fatal):', storageError);
        }
      }

      // 2. Null profiles.avatar_url — this MUST succeed.
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', userId);
      if (profileError) throw profileError;

      // 3. Null brokers.avatar_url — best-effort, don't block on failure (user may not be a broker).
      const brokerClear = brokerId
        ? supabase.from('brokers').update({ avatar_url: null }).eq('id', brokerId)
        : userEmail
          ? supabase.from('brokers').update({ avatar_url: null }).eq('email', userEmail)
          : null;
      if (brokerClear) {
        const { error: brokerError } = await brokerClear;
        if (brokerError) console.warn('Broker update error (non-fatal):', brokerError);
      }

      // 4. Update local state and notify parent.
      setPreview(null);
      if (onUploadComplete) onUploadComplete(null);
    } catch (err) {
      console.error('Remove failed:', err);
      setError(err.message || 'Failed to remove');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="brokerAvatarUpload">
      <div className="brokerAvatarUpload__preview">
        {preview ? (
          <img src={preview} alt="Profile" className="brokerAvatarUpload__image" />
        ) : (
          <div className="brokerAvatarUpload__placeholder">
            <Camera size={32} />
          </div>
        )}
        {(uploading || removing) && (
          <div className="brokerAvatarUpload__overlay">
            <Loader size={24} className="brokerAvatarUpload__spinner" />
          </div>
        )}
      </div>

      <div className="brokerAvatarUpload__actions">
        <button
          type="button"
          className="brokerAvatarUpload__button brokerAvatarUpload__button--primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || removing}
        >
          <Camera size={16} />
          {preview ? 'Change photo' : 'Upload photo'}
        </button>

        {preview && (
          <button
            type="button"
            className="brokerAvatarUpload__button brokerAvatarUpload__button--secondary"
            onClick={() => setShowRemoveConfirm(true)}
            disabled={uploading || removing}
          >
            <X size={16} />
            {removing ? 'Removing…' : 'Remove'}
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={showRemoveConfirm}
        title="Remove profile picture?"
        message="Your profile photo will be deleted. You can upload a new one anytime."
        confirmLabel="Yes, remove"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleRemove}
        onCancel={() => setShowRemoveConfirm(false)}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {error && <p className="brokerAvatarUpload__error">{error}</p>}
      <p className="brokerAvatarUpload__hint">JPG, PNG or WEBP · Max 5MB · Square photos work best</p>
    </div>
  );
}
